import { c as _c } from "react/compiler-runtime";
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { hashKey, type QueryClient, useIsFetching, useQueryClient } from '@tanstack/react-query';
import { type PointerEvent, useEffect, useRef } from 'react';
import { useUnmount } from 'usehooks-ts';
import { getOnboardingQueries } from '@/components/entity-onboarding-details';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import useEntityId from '@/entity/hooks/use-entity-id';
import usePermissions from '@/hooks/use-permissions';
type QueryName = keyof ReturnType<typeof getOnboardingQueries>;
type IntentHandlers = {
  onPointerEnter: (event: PointerEvent) => void;
  onPointerLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onPointerDown: (event: PointerEvent) => void;
};

// Exclude Sentilink (a POST), business insights (requires decrypt access), and AML hits (a decrypt).
const PREFETCHED_QUERIES: QueryName[] = ['attributes', 'documents', 'riskSignals', 'matchSignals', 'trace', 'ruleSetResult', 'dupes'];

// Ignore a pointer sweeping the strip or a held arrow key.
const INTENT_DELAY_MS = 80;

// Limit background requests to nearby onboardings.
const IDLE_PREFETCH_COUNT = 3;
type UsePrefetchOnboardingsOptions = {
  onboardings: EntityOnboarding[];
  /** The open onboarding; none while Current is open. */
  selected?: EntityOnboarding;
  isEnabled: boolean;
};
const usePrefetchOnboardings = t0 => {
  const $ = _c(32);
  const {
    onboardings,
    selected,
    isEnabled
  } = t0;
  const queryClient = useQueryClient();
  const {
    isAuditor
  } = usePermissions();
  const entityId = useEntityId();
  const {
    kind
  } = useEntityContext();
  const intentTimerRef = useRef(undefined);
  let entity;
  let t1;
  if ($[0] !== entityId || $[1] !== kind || $[2] !== selected) {
    entity = {
      id: entityId,
      kind
    };
    t1 = selected ? Object.values(getOnboardingQueries(entity, selected)).filter(_temp).map(_temp2) : [];
    $[0] = entityId;
    $[1] = kind;
    $[2] = selected;
    $[3] = entity;
    $[4] = t1;
  } else {
    entity = $[3];
    t1 = $[4];
  }
  const selectedHashes = t1;
  let t2;
  if ($[5] !== selected || $[6] !== selectedHashes) {
    t2 = {
      predicate: query => !selected || selectedHashes.includes(query.queryHash)
    };
    $[5] = selected;
    $[6] = selectedHashes;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isSelectedSettled = useIsFetching(t2) === 0;
  let t3;
  if ($[8] !== onboardings) {
    t3 = onboardings.map(_temp3).join();
    $[8] = onboardings;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const onboardingIds = t3;
  let t4;
  if ($[10] !== isAuditor) {
    t4 = isAuditor ? PREFETCHED_QUERIES.filter(_temp4) : PREFETCHED_QUERIES;
    $[10] = isAuditor;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const queryNames = t4;
  let t5;
  if ($[12] !== entity || $[13] !== queryClient || $[14] !== queryNames) {
    t5 = onboarding => {
      const queries = getOnboardingQueries(entity, onboarding);
      return Promise.all(queryNames.map(name_0 => {
        const options_1 = queries[name_0];
        const state = queryClient.getQueryState(options_1.queryKey);
        if (options_1.enabled === false || state?.data !== undefined || state?.error) {
          return;
        }
        return queryClient.prefetchQuery(options_1 as Parameters<QueryClient['prefetchQuery']>[0]);
      }));
    };
    $[12] = entity;
    $[13] = queryClient;
    $[14] = queryNames;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const prefetch = t5;
  let t6;
  if ($[16] !== isEnabled || $[17] !== isSelectedSettled || $[18] !== onboardings || $[19] !== prefetch || $[20] !== selected) {
    t6 = () => {
      if (!isEnabled || !isSelectedSettled) {
        return;
      }
      let isCancelled = false;
      const selectedIndex = selected ? onboardings.findIndex(t7 => {
        const {
          id: id_0
        } = t7;
        return id_0 === selected.id;
      }) : -1;
      const queue = onboardings.map((onboarding_0, index) => ({
        onboarding: onboarding_0,
        distance: Math.abs(index - selectedIndex)
      })).filter(t8 => {
        const {
          onboarding: onboarding_1
        } = t8;
        return onboarding_1.id !== selected?.id;
      }).sort(_temp5).slice(0, IDLE_PREFETCH_COUNT);
      const prefetchQueue = async () => {
        for (const {
          onboarding: onboarding_2
        } of queue) {
          if (isCancelled) {
            return;
          }
          await prefetch(onboarding_2);
        }
      };
      const hasIdleCallback = typeof window.requestIdleCallback === "function";
      const id_1 = hasIdleCallback ? window.requestIdleCallback(prefetchQueue) : window.setTimeout(prefetchQueue, 200);
      return () => {
        isCancelled = true;
        if (hasIdleCallback) {
          window.cancelIdleCallback(id_1);
        } else {
          window.clearTimeout(id_1);
        }
      };
    };
    $[16] = isEnabled;
    $[17] = isSelectedSettled;
    $[18] = onboardings;
    $[19] = prefetch;
    $[20] = selected;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = selected?.id;
  let t8;
  if ($[22] !== entityId || $[23] !== isAuditor || $[24] !== isEnabled || $[25] !== isSelectedSettled || $[26] !== onboardingIds || $[27] !== t7) {
    t8 = [entityId, t7, isSelectedSettled, onboardingIds, isAuditor, isEnabled];
    $[22] = entityId;
    $[23] = isAuditor;
    $[24] = isEnabled;
    $[25] = isSelectedSettled;
    $[26] = onboardingIds;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  useEffect(t6, t8);
  let t9;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => window.clearTimeout(intentTimerRef.current);
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const cancelIntent = t9;
  useUnmount(cancelIntent);
  let t10;
  if ($[30] !== prefetch) {
    const getIntentHandlers = onboarding_3 => {
      const prefetchNow = () => {
        cancelIntent();
        prefetch(onboarding_3);
      };
      const schedule = () => {
        cancelIntent();
        intentTimerRef.current = window.setTimeout(prefetchNow, INTENT_DELAY_MS);
      };
      return {
        onPointerEnter: event => {
          if (event.pointerType !== "touch") {
            schedule();
          }
        },
        onPointerLeave: cancelIntent,
        onFocus: schedule,
        onBlur: cancelIntent,
        onPointerDown: event_0 => {
          if (event_0.pointerType !== "touch") {
            prefetchNow();
          }
        }
      };
    };
    t10 = {
      getIntentHandlers
    };
    $[30] = prefetch;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};
export type { IntentHandlers };
export default usePrefetchOnboardings;
function _temp(options) {
  return options.enabled !== false;
}
function _temp2(options_0) {
  return hashKey(options_0.queryKey);
}
function _temp3(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp4(name) {
  return name !== "dupes";
}
function _temp5(a, b) {
  return a.distance - b.distance;
}